/**
 * The org's look, saved with the org (the 2026-10-06 next-phase plan, step 3): one line over the theme editor that
 * says whose the look is and, with edits not yet saved, offers to save them for the org (an org admin) or to drop
 * them. Light or dark stays each person's own. No hooks: the page passes the theme admin's orgLook state.
 */
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import type { OrgLookState } from "@/lib/tenant-theme";

export function OrgLookBar({ orgName, state }: { orgName: string | undefined; state: OrgLookState }) {
  const org = orgName?.trim() || "this org";
  if (!state.dirty) {
    return (
      <p data-org-look="saved" className="text-xs text-on-surface-muted">
        {`The look is saved with ${org} and applies to everyone in it. Light or dark is each person's own choice.`}
      </p>
    );
  }
  return (
    <div
      data-org-look="dirty"
      className="flex flex-wrap items-center gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
    >
      <p className="min-w-0 flex-1 text-on-surface">
        {state.canSave
          ? `These changes show only to you until you save them for ${org}.`
          : `These changes show only to you: only an org admin of ${org} can save its look.`}
      </p>
      <Button size="sm" variant="ghost" onClick={state.discard} disabled={state.saving}>
        Discard
      </Button>
      {state.canSave && (
        <Button size="sm" onClick={() => void state.save()} disabled={state.saving}>
          {state.saving && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Save for the org
        </Button>
      )}
      {state.error && (
        <p role="alert" className="w-full text-xs text-danger">
          {state.error}
        </p>
      )}
    </div>
  );
}
